import { Lightning, Lightning as lng, Log, Utils } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { DetailDescription } from './DetailDescription';
import { getDetailDataSource } from '../../Business/detail/DetailDSUtil';
import { fullUrlWithRecipe, Recipes } from '../../Business/utility/ImageRecipeUtil';
import { DetailActionRow } from './DetailActionRow';
import { DetailTranslationKeys, ErrorTranslationKeys } from '../../api/models/TranslationKeys';
import { _constants } from '../../components/MiniDetail';
import { AssetType } from '../../api/models/Shared.types';
import { CmsConstants } from '../../Business/cms/CmsConstants';
import { RibbonDataSources } from '../../Business/detail/detail.types';
import { Spinner } from '../../components/shared/Spinner';

const Constants = {
  Description: {
    marginLeft: 48,
    marginTop: 42,
  },
  PosterImage: {
    width: 1177,
    height: 662,
    Gradients: {
      endPoint: 0.5,
      startColor: 0xff000000,
      endColor: Theme.Colors.Transparent,
    },
  },
};

export class DetailPage extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: h => h,
      Background: {
        w: w => w,
        h: h => h,
        color: Theme.Colors.Primary,
        src: Utils.asset('/images/detail/background_gradient.png'),
      },
      Content: {
        x: 0,
        y: 0,
        w: w => w,
        flex: { direction: 'column' },
        Poster: {
          flexItem: false,
          x: w => w,
          mountX: 1,
          w: Constants.PosterImage.width,
          h: Constants.PosterImage.height,
          PosterImage: {
            shader: {
              type: Lightning.shaders.FadeOut,
              left: 700,
              bottom: 400,
            },
          },
        },
        DetailDescription: {
          flexItem: { marginTop: Constants.Description.marginTop },
          x: Constants.Description.marginLeft,
          type: DetailDescription,
        },
        Controls: {
          flexItem: { marginTop: 40 },
          x: Constants.Description.marginLeft,
          w: 910,
          h: 100,
          type: DetailActionRow,
        },
        Ribbons: {
          flexItem: { marginTop: 30 },
          flex: { direction: 'column' },
          w: w => w,
        },
        RibbonSpinner: {
          flexItem: {},
          type: Spinner,
          mountX: 0.5,
          x: w => w / 2,
        },
      },
    };
  }

  _init() {
    const backend = this.params.backend;
    this.focusIndex = 0;
    this.tag('DetailDescription').params = { backend };
    this.initialize();
  }

  _active() {
    this.dataSource.logPageView();
  }

  initialize() {
    Log.info('detailpage received type and id: ', this.params.assetType, this.params.assetId);
    if (!this.params.assetType || !this.params.assetId) {
      Log.error('missing type or id on detailpage');
      return;
    }
    let type = this.params.assetType;
    this.dataSource = getDetailDataSource(this.params.backend, type);
    this.$reloadDetailContent(true);
  }

  $reloadDetailContent(reloadRibbons = false) {
    let id = this.params.assetId;
    if (reloadRibbons) {
      this.tag('Ribbons').childList.clear();
      this.tag('RibbonSpinner').visible = true;
    }

    this.dataSource
      .load(id)
      .then(contentData => {
        if (this.dataSource.program?.adult) {
          this._showErrorDialog(this.params.backend.translations.forKey(DetailTranslationKeys.detail_adult_warning));
          return;
        }
        this.tag('PosterImage').src = fullUrlWithRecipe(
          this.params.backend,
          contentData.images.detailPoster,
          Recipes.DetailPosterRecipe
        );
        contentData.description
          .load()
          .then(descr => {
            this.tag('DetailDescription').content = descr;
            // load ribbons after the description has been loaded (preference to description in loading order)
            this.tag('Controls').setButtons(contentData, this.params.backend, this.params.modalOwner);
            if (reloadRibbons) {
              // this.tag('Ribbons').childList.clear();
              this.setRibbons(contentData.ribbonDatasources);
            }
            this._refocus();

            this.tag('DetailDescription').onUpdate = e => {
              if (e.finalH < 400) e.h = 400;
              else e.h = 0; // enables autosizing for flexbox
            };
          })
          .catch(err => {
            this.tag('RibbonSpinner').visible = false;
            this._showErrorDialog(err);
          });
      })
      .catch(() => {
        if (this.dataSource.schedule) {
          const id = this.dataSource.schedule.scheduleId;
          // convert data source to other datasource and try again
          this.dataSource = getDetailDataSource(this.params.backend, AssetType.SCHEDULE);

          this.dataSource.load(id).then(contentData => {
            this.tag('PosterImage').src = fullUrlWithRecipe(
              this.params.backend,
              contentData.images.detailPoster,
              Recipes.DetailPosterRecipe
            );
            contentData.description
              .load()
              .then(descr => {
                this.tag('DetailDescription').content = descr;
                // load ribbons after the description has been loaded (preference to description in loading order)
                this.tag('Controls').setButtons(contentData, this.params.backend, this.params.modalOwner);
                if (reloadRibbons) {
                  this.tag('Ribbons').childList.clear();
                  this.setRibbons(contentData.ribbonDatasources);
                }
                this._refocus();

                this.tag('DetailDescription').onUpdate = e => {
                  if (e.finalH < 400) e.h = 400;
                  else e.h = 0; // enables autosizing for flexbox
                };
              })
              .catch(err => {
                this.tag('RibbonSpinner').visible = false;
                this._showErrorDialog(err);
              });
          });
        } else {
          // cant convert to schedule so close detail page
          this._handleBack();
        }
      });
  }

  _centerZero() {
    this.tag('Content').setSmooth('y', 0);
  }

  _centerVertical(centerY) {
    // Workaround for modals
    // Showing/hiding a modal will cause a refocus event, at which point this.finalH === 0
    // Prevent recentering in that case
    if (this.finalH === 0) return;

    centerY +=
      this.tag('DetailDescription').finalH +
      this.tag('DetailDescription').flexItem.marginTop +
      this.tag('Controls').finalH +
      this.tag('Controls').flexItem.marginTop;

    let target = this.finalH / 2;

    if (this._getFocusable()[this.focusIndex].data.cmsProperties) {
      if (!this._getFocusable()[this.focusIndex].data.cmsProperties.includes('HIDE_MINI_DETAIL')) {
        target = (1080 - _constants.miniDetailHeight) / 2;
      }
    } else {
      target = (1080 - _constants.miniDetailHeight) / 2;
    }

    const result = Math.min(
      0, //-this.tag('Content').finalH + this.tag('Content').finalH - constants.marginBottom,
      target - centerY
    );
    // console.log('centerVertical', centerY, target, result, this.tag('Content').finalH);

    this.tag('Content').setSmooth('y', result);
  }

  /**
   * @param {RibbonDataSources} ribbonDataSources
   */
  setRibbons(ribbonDataSources) {
    let ribbonsTag = this.tag('Ribbons');
    if (!ribbonDataSources) return;
    Promise.all(ribbonDataSources.filter(x => x !== undefined).map(x => x.load())).then(() => {
      ribbonDataSources
        .filter(x => x && !x.noData())
        .forEach(ds => {
          ribbonsTag.childList.a({
            flexItem: { marginBottom: 20 },
            signals: { centerVertical: '_centerVertical' },
            contentOffset: CmsConstants.Ribbon.defaultContentOffset,
            ...ds.componentDescription(),
          });
        });
      this.tag('RibbonSpinner').visible = false;
    });
  }

  _showErrorDialog(err) {
    this.fireAncestors(
      '$showMessage',
      err,
      this.params.backend.translations.forKey(ErrorTranslationKeys.error_title),
      () => {
        this._handleBack();
      }
    );
  }

  _handleUp() {
    this.focusIndex = Math.max(this.focusIndex - 1, 0);
    if (this.focusIndex === 0) {
      this._centerZero();
    }
  }

  _handleDown() {
    this.focusIndex = Math.min(this.focusIndex + 1, this._getFocusable().length - 1);
  }

  _getFocused() {
    return this._getFocusable()[this.focusIndex];
  }

  _getFocusable() {
    return [this.tag('Controls'), ...this.tag('Ribbons').children];
  }

  _handleBack() {
    this.fireAncestors('$closeModal');
    // return false;
  }
}
